/**
 * Cuándo sale la campaña y «Revisar y mandar»: el chequeo con el que se confirma y el confirmar.
 *
 * - «Ahora» o «Programar» se elige a propósito en «Cuándo» (29/10/2026): no hay uno por defecto (`modoCuando` null) y
 *   hasta entonces no se manda. Si se llega a «Revisar y mandar» sin elegir (una copia llega directo), se elige ahí
 *   mismo (`elegirEnRevisar`). La fecha y hora programadas se leen en la zona de la campaña. Lo elegido se guarda en la
 *   pestaña (`guardarCuando`), así un F5 en «Revisar y mandar» no lo pierde. El reloj (`ahora`, del que salen los límites
 *   de la fecha) corre en los dos pasos.
 * - Una repetición que quedó en borrador (29/10/2026): «Programar» viene con la fecha que le toca, si todavía no pasó
 *   (`fechaDeLaRepeticion`). Es una sugerencia a la vista, como la franja: no marca ninguno de los dos.
 * - El chequeo se pide al entrar a «Revisar y mandar» y ante cada cambio de «cuándo» o del horario (debounce de 500 ms;
 *   al entrar, enseguida): cada chequeo anota en la auditoría. Desde el cambio hasta que llega el nuevo, el botón está
 *   apagado (`pendiente`): el de antes era de otro «cuándo». Vale 10 minutos (`VIGENCIA_CHEQUEO_MS`) desde que LLEGÓ,
 *   según el reloj de esta pantalla (`recibido`): con el `calculadoAt` del servidor contra el reloj de la PC, una PC
 *   adelantada nunca podía confirmar. El botón se apaga solo y, al volver a la pestaña, se recalcula. En «Cuándo» no se
 *   pide ninguno.
 * - Otra pestaña pudo cambiar la campaña (29/10/2026): al entrar a «Revisar y mandar» y ante un 409 se vuelve a traer
 *   (`releer`, de la página). Si cambió algo que no son cifras (la plantilla, las variables, a quién, el horario, la baja),
 *   se dice cómo quedó (`avisoOtraPestana`) y se vuelven a pedir el chequeo y la previa; antes, un 409 sólo cambiaba los
 *   números y el aviso decía «alguien se dio de baja». Mientras se trae, no se confirma.
 * - El horario que se manda (`horario`) sale del límite de Meta del último chequeo (o de la cuenta mientras no hay
 *   chequeo) y, en «Revisar y mandar», de lo GUARDADO (29/10/2026): el horario y la baja que se repasan y se mandan son
 *   los de la campaña, nunca lo editado en «Cuándo» sin guardar. Va siempre en el chequeo y en el confirmar, también
 *   `horario: null` (la franja de la marca): para el backend, sin horario es «el guardado».
 *   En «Cuándo», el editado: es el que guarda «Siguiente».
 * - Se manda con una prueba que llegó o con «Seguir sin probar» (`sinPrueba`), en la misma pantalla.
 * - Confirmar manda la huella y la cantidad del último chequeo: si algo cambió (una baja, el saldo, el cupo), el backend
 *   responde 409 `CHEQUEO_DESACTUALIZADO` con el chequeo nuevo, que reemplaza al mostrado. No se reintenta solo: el
 *   operador tiene que ver los números nuevos antes de gastar (y, si cambió algo más, también eso). Un 422 trae la lista
 *   de bloqueos.
 * - Un 5xx, un corte o una respuesta que no llega en `ESPERA_CONFIRMAR_MS` no dicen si salió (29/10/2026): antes de
 *   ofrecer otra vez se averigua con `GET /campanas/:id`. Si ya no es un borrador, «Ya se confirmó» con el link al
 *   detalle, sin reintentar; si sigue en borrador, «Reintentar»; si tampoco se pudo ver, no se manda hasta volver a
 *   fijarse. Antes decía «Probá de nuevo» sin saber si había salido.
 * - Un envío grande (`logic/vasAMandar.ts`) pide escribir la cantidad de personas antes de mandar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CampanaDetalle, Chequeo, CodigoBloqueo, CuentaPublica, HorarioCampana, Previa, ResultadoConfirmar } from "../../types";
import { oficialApi } from "../../api";
import type { PasoAsistente } from "../../logic/chequeo";
import { puedeConfirmar, textoBotonConfirmar, VIGENCIA_CHEQUEO_MS } from "../../logic/chequeo";
import { pideRefuerzo, refuerzoCumplido, textoRefuerzo } from "../../logic/vasAMandar";
import { TEXTO_ERROR_CLAVE, conLimite, esErrorDeClave, leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { ZONA_MARCA, fechaHoraEnZona, isoALocalEnZona, limitesProgramacionEnZona, localEnZonaAIso, paisDeZona } from "../../logic/zonas";
import { erroresHorario, horarioParaEnviar, type HorarioEnEdicion } from "../../logic/horario";
import type { EleccionBaja } from "../../logic/baja";
import { guardarCuando, leerCuando, pasoQueArregla, type CtaPaso } from "../../logic/asistente";
import { fechaQueLeToca, textoFechaQueLeToca } from "../../logic/repeticion";

const B = "/activa/oficial";

/**
 * Por qué no salió el confirmar: el texto, los bloqueos del 422, la próxima apertura de la franja o el link a la campaña.
 * Sin respuesta del servidor: `reintentar` (sigue en borrador, no salió) o `averiguar` (no se pudo saber cómo quedó).
 * `paso`: se arregla en ese paso (`pasoQueArregla`), no reintentando.
 */
export type RechazoConfirmar = {
  texto: string; bloqueos?: CodigoBloqueo[]; proximaApertura?: string; linkDetalle?: boolean; reintentar?: boolean; averiguar?: boolean;
  paso?: PasoAsistente;
};

/** Cuánto se espera la respuesta del confirmar: después, como con un corte, se averigua si salió. */
export const ESPERA_CONFIRMAR_MS = 90_000;
export const TEXTO_YA_CONFIRMADA = "Esta campaña ya se confirmó.";
export const TEXTO_FECHA_PASADA = "Esa hora ya pasó (o falta menos de 5 minutos): elegí una más adelante.";
export const TEXTO_FECHA_LEJANA = "Se puede programar hasta 30 días adelante: elegí una fecha más cercana.";
export const TEXTO_SIN_RESPUESTA_CONFIRMADA = "Ya se confirmó: la respuesta no llegó, pero la campaña ya no es un borrador.";
export const TEXTO_SIN_RESPUESTA_BORRADOR = "No llegó la respuesta del servidor, pero la campaña sigue en borrador: no salió.";
export const TEXTO_NO_SE_SABE = "No sabemos si salió: no llegó la respuesta y tampoco pudimos ver cómo quedó la campaña. Fijate antes de volver a mandar.";

/**
 * Lo que dice la campaña vuelta a traer (`releer`): si otra pestaña cambió algo que no son cifras, el texto para avisarlo
 * (null si no cambió nada); `confirmada` si ya no es un borrador. null si no se pudo traer.
 */
export type CampanaReleida = { texto: string | null; confirmada: boolean } | null;

/** El «cuándo» y el horario con los que se pidió un chequeo: si cambian, ese chequeo ya no es el de la pantalla. */
const claveDelPedido = (cuando: string | null, horario: HorarioCampana | null) => JSON.stringify([cuando, horario]);

export function useConfirmarCampana({ paso, idCampana, cargando, horarioEd, guardado, cuenta, pruebaOk, sinPrueba, previa, setPrevia, releer, fechaDeLaRepeticion }: {
  paso: PasoAsistente;
  idCampana: string;
  /** la campaña se está cargando: entrar a «Revisar y mandar» desde otro paso cuenta recién después */
  cargando: boolean;
  /** el horario de «Cuándo», como se está editando */
  horarioEd: HorarioEnEdicion;
  /** lo guardado en la campaña: el horario (como en «Cuándo»), si ya se eligió, y la baja ("" sin elegir) */
  guardado: { horario: HorarioEnEdicion; horarioElegido: boolean; baja: EleccionBaja | "" };
  /** el límite de Meta de la cuenta, mientras no hay un chequeo */
  cuenta: CuentaPublica | null;
  /** la prueba llegó; si no, se manda sólo con «Seguir sin probar» marcado (`sinPrueba`) */
  pruebaOk: boolean;
  sinPrueba: boolean;
  /** la previa de las variables (`useVariablesCampana`): si no está, «Revisar y mandar» la pide */
  previa: Previa | null;
  setPrevia: (p: Previa | null) => void;
  /** vuelve a traer la campaña y, si otra pestaña la cambió, la aplica (`CampanaReleida`) */
  releer: () => Promise<CampanaReleida>;
  /** sólo en una repetición: la fecha que le toca (`repeticionDe.programadaPara`) */
  fechaDeLaRepeticion?: string | null;
}) {
  const navigate = useNavigate();
  // Sin modo preseleccionado (29/10/2026): «Ahora» o «Programar» se elige a propósito; hasta entonces no se manda.
  const [modoCuando, setModoCuando] = useState<"ahora" | "programar" | null>(null);
  /** La confirmación reforzada de un envío grande: la cantidad de personas, escrita (`logic/vasAMandar`). */
  const [cantidadEscrita, setCantidadEscrita] = useState("");
  /** La previa real en «Revisar y mandar» cuando no vino de las variables (un F5): "" = pedirla, "pidiendo", "error". */
  const [previaConfirmar, setPreviaConfirmar] = useState<"" | "pidiendo" | "error">("");
  const [cuandoLocal, setCuandoLocal] = useState("");
  const [chequeo, setChequeo] = useState<Chequeo | null>(null);
  /** cuándo llegó el chequeo (el reloj de esta pantalla) y con qué «cuándo» y horario se pidió (`claveDelPedido`) */
  const [recibido, setRecibido] = useState<{ en: number; clave: string } | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [errorChequeo, setErrorChequeo] = useState("");
  /** el error del chequeo se arregla en otro paso (un público borrado, una variable sin dato): «Ir a …», no «Reintentar» */
  const [errorChequeoPaso, setErrorChequeoPaso] = useState<PasoAsistente | null>(null);
  const [avisoDesactualizado, setAvisoDesactualizado] = useState(false);
  /** otra pestaña cambió la campaña: cómo quedó (`textoCambioEnOtraPestana`) */
  const [avisoOtraPestana, setAvisoOtraPestana] = useState("");
  /** se está volviendo a traer la campaña: hasta saber si cambió, no se confirma */
  const [releyendo, setReleyendo] = useState(false);
  /** sube cuando la campaña cambió en otra pestaña: el chequeo se vuelve a pedir con lo nuevo */
  const [version, setVersion] = useState(0);
  /** ya no es un borrador (se confirmó, acá sin respuesta o en otra pestaña): no se ofrece mandar */
  const [yaConfirmada, setYaConfirmada] = useState(false);
  /** sin respuesta del confirmar, se está viendo cómo quedó la campaña */
  const [averiguando, setAveriguando] = useState(false);
  const [rechazo, setRechazo] = useState<RechazoConfirmar | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [ahora, setAhora] = useState(() => new Date());
  const seqChequeo = useRef(0);
  // En «Revisar y mandar», el horario guardado: es el que se repasa y el que sale. En «Cuándo», el que se está editando.
  const h = paso === "revisar" ? guardado.horario : horarioEd;

  // Lo elegido en «Cuándo», de vuelta después de un F5 (una vez por campaña, cuando se sabe cuál es). DURANTE el render y
  // no en un efecto, como en `contexto.tsx`: el chequeo de entrada sale enseguida, y con un efecto salía uno de más, con
  // «Ahora», antes de ver que estaba programada.
  const [cuandoDe, setCuandoDe] = useState("");
  /** una repetición: la fecha que le toca, mientras todavía se puede programar */
  const sugerida = fechaQueLeToca(fechaDeLaRepeticion, ahora);
  if (idCampana && cuandoDe !== idCampana) {
    setCuandoDe(idCampana);
    const g = leerCuando(idCampana);
    if (g) { setModoCuando(g.modo); setCuandoLocal(g.local); }
    // una repetición: «Programar» viene con su fecha, sin marcarlo
    else if (sugerida) setCuandoLocal(isoALocalEnZona(sugerida, h.zona || ZONA_MARCA));
  }
  /**
   * Se llegó a «Revisar y mandar» sin «Ahora» ni «Programar» (una copia llega directo, 29/10/2026): se elige ahí mismo, y
   * queda a la vista para cambiarlo (con «Programar», la fecha va en el mismo lugar). Después de recuperar lo de la
   * pestaña: con algo elegido ahí, no hace falta.
   */
  const [elegirEnRevisar, setElegirEnRevisar] = useState(false);
  if (paso === "revisar" && idCampana && cuandoDe === idCampana && modoCuando === null && !elegirEnRevisar) setElegirEnRevisar(true);
  /** Elige «Ahora» o «Programar» (y la fecha y hora) y lo deja en la pestaña. */
  const elegirCuando = useCallback((modo: "ahora" | "programar", local: string) => {
    setModoCuando(modo);
    setCuandoLocal(local);
    if (idCampana) guardarCuando(idCampana, { modo, local });
  }, [idCampana]);

  // La fecha y hora programadas se leen en la zona de la campaña (26/10): antes, en la del navegador de quien la cargaba.
  const zona = h.zona || ZONA_MARCA;
  const cuandoIso = modoCuando === "programar" ? localEnZonaAIso(cuandoLocal, zona) : null;
  const fechaFaltante = modoCuando === "programar" && !cuandoIso;
  const limites = useMemo(() => limitesProgramacionEnZona(ahora, zona), [ahora, zona]);
  /**
   * Con «Programar», una hora que ya pasó (o falta menos de 5 minutos) o a más de 30 días no sale: se dice al lado de la
   * fecha y el botón se apaga (el campo tiene `min` y `max`, pero escrita a mano pasaba igual y lo decía recién el
   * backend). Las dos van en la zona de la campaña, como `limites`.
   */
  const fechaFueraDeRango = modoCuando === "programar" && cuandoIso
    ? (cuandoLocal < limites.min ? TEXTO_FECHA_PASADA : cuandoLocal > limites.max ? TEXTO_FECHA_LEJANA : "")
    : "";
  // El horario propio de la campaña (opcional). Mal cargado no se pide el chequeo: se dice qué falta. El tope se
  // compara con el límite de Meta del último chequeo (sin el freno por calidad, como el backend). En «Cuándo» todavía
  // no hay chequeo: el de la cuenta.
  const limiteMeta = chequeo?.cupo24h
    ? (!chequeo.cupo24h.ilimitado ? (chequeo.cupo24h.limiteMeta ?? chequeo.cupo24h.limite) : null)
    : (cuenta?.limiteMeta && !cuenta.limiteMeta.ilimitado ? cuenta.limiteMeta.limite : null);
  const erroresDelHorario = erroresHorario(h, limiteMeta);
  const horarioIncompleto = erroresDelHorario.length > 0;
  const horario = useMemo(() => horarioParaEnviar(h, limiteMeta), [h, limiteMeta]);
  const claveHorario = JSON.stringify(horario);
  /** El chequeo con su hora de llegada y su pedido, o nada. */
  const ponerChequeo = useCallback((c: Chequeo | null, clave = "") => {
    setChequeo(c);
    setRecibido(c ? { en: Date.now(), clave } : null);
  }, []);

  const calcularChequeo = useCallback(async (c: string | null, horarioDelPedido: HorarioCampana | null) => {
    if (!idCampana) return;
    const mio = ++seqChequeo.current;
    setCalculando(true);
    setErrorChequeo("");
    setErrorChequeoPaso(null);
    try {
      // el horario va siempre, también null (la franja de la marca): sin él, el backend usa el guardado
      const l = leerRespuesta<Chequeo>(await oficialApi.chequeo(idCampana, c, horarioDelPedido));
      if (mio !== seqChequeo.current) return;
      if (!l.ok) { ponerChequeo(null); setErrorChequeo(l.error || textoError(l)); setErrorChequeoPaso(pasoQueArregla(l.code)); return; }
      ponerChequeo(l.data, claveDelPedido(c, horarioDelPedido));
      setAhora(new Date());
    } catch (e) {
      if (mio !== seqChequeo.current) return;
      ponerChequeo(null);
      const x = leerError(e);
      setErrorChequeo(textoError(x));
      setErrorChequeoPaso(pasoQueArregla(x.code));
    } finally {
      if (mio === seqChequeo.current) setCalculando(false);
    }
  }, [idCampana, ponerChequeo]);

  // Al entrar y ante cada cambio de «cuándo» o del horario (debounce 500 ms; al entrar, enseguida).
  const hayChequeo = !!chequeo;
  useEffect(() => {
    if (paso !== "revisar" || !idCampana || fechaFaltante || horarioIncompleto) return;
    const t = setTimeout(() => { void calcularChequeo(cuandoIso, horario); }, hayChequeo ? 500 : 0);
    return () => clearTimeout(t);
    // `hayChequeo` sólo decide la demora: no tiene que disparar otro cálculo. `claveHorario` es el horario por valor y
    // `version`, la campaña cambiada en otra pestaña.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, idCampana, cuandoIso, fechaFaltante, horarioIncompleto, claveHorario, calcularChequeo, version]);
  /** el chequeo a la vista se pidió con otro «cuándo» u otro horario: hasta que llegue el nuevo, no vale */
  const pendiente = !!chequeo && recibido?.clave !== claveDelPedido(cuandoIso, horario);

  /**
   * La campaña vuelta a traer (al entrar a «Revisar y mandar» y ante un 409). Si otra pestaña cambió algo que no son
   * cifras, lo dice y se vuelven a pedir el chequeo (con lo nuevo) y la previa; con un 409, la previa siempre (las
   * personas pudieron cambiar). Confirmada en otra pestaña, no se ofrece mandar.
   */
  const releerRef = useRef(releer);
  releerRef.current = releer;
  const revisarOtraPestana = useCallback(async (por409: boolean) => {
    setReleyendo(true);
    try {
      const r = await releerRef.current();
      if (!r) return;
      if (r.confirmada) { setYaConfirmada(true); setRechazo({ texto: TEXTO_YA_CONFIRMADA, linkDetalle: true }); return; }
      if (r.texto) {
        seqChequeo.current++;
        setCalculando(false);
        ponerChequeo(null);
        setAvisoDesactualizado(false);
        setVersion((v) => v + 1);
      }
      if (r.texto || !por409) setAvisoOtraPestana(r.texto || "");
      if (r.texto || por409) { setPrevia(null); setPreviaConfirmar(""); }
    } finally {
      setReleyendo(false);
    }
  }, [ponerChequeo, setPrevia]);
  // Al entrar a «Revisar y mandar» desde otro paso (no al cargar: la campaña recién se trajo).
  const pasoVisto = useRef<PasoAsistente | null>(null);
  useEffect(() => {
    if (cargando || !idCampana) return;
    const antes = pasoVisto.current;
    pasoVisto.current = paso;
    if (paso === "revisar" && antes !== null && antes !== "revisar") void revisarOtraPestana(false);
  }, [paso, idCampana, cargando, revisarOtraPestana]);

  // El reloj, en «Cuándo» (los límites de la fecha) y en «Revisar y mandar». Ahí, además, el chequeo vale 10 minutos: el
  // botón se apaga solo y, al volver a la pestaña, se recalcula.
  useEffect(() => {
    if (paso !== "cuando" && paso !== "revisar") return;
    const t = setInterval(() => setAhora(new Date()), 30_000);
    const alVolver = () => {
      if (document.hidden) return;
      if (paso !== "revisar" || !chequeo || !recibido) { setAhora(new Date()); return; }
      if (!(Date.now() - recibido.en < VIGENCIA_CHEQUEO_MS)) void calcularChequeo(cuandoIso, horario);
      else setAhora(new Date());
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", alVolver); };
  }, [paso, chequeo, recibido, cuandoIso, horario, calcularChequeo]);

  const usarHora = (iso: string) => {
    elegirCuando("programar", isoALocalEnZona(iso, zona));
    setRechazo(null);
  };

  const chequeoViejo = !!chequeo && !(!!recibido && ahora.getTime() - recibido.en < VIGENCIA_CHEQUEO_MS);
  /** calculando, esperando el chequeo de lo que se cambió o trayendo la campaña de nuevo: el botón espera */
  const esperando = calculando || pendiente || releyendo;
  // El horario y la baja son obligatorios también para los borradores de antes de elegirlos: sin elegir (y guardar), no se
  // confirma.
  const faltaHorarioYBaja = !guardado.horarioElegido ? "Elegí el horario en «Cuándo»" : !guardado.baja ? "Elegí cómo se dan de baja en «Cuándo»" : "";
  /** sin una prueba que llegó, hay que marcar «Seguir sin probar» */
  const faltaPrueba = !pruebaOk && !sinPrueba;
  /** un envío grande pide escribir la cantidad de personas, y todavía no está (o no es la de este chequeo) */
  const refuerzoPendiente = !!chequeo && pideRefuerzo(chequeo) && !refuerzoCumplido(cantidadEscrita, chequeo.envio);
  /** sin saber si salió (o sabiendo que sí), no se manda otra vez */
  const sinSaberSiSalio = yaConfirmada || averiguando || !!rechazo?.averiguar;
  const sePuedeConfirmar = puedeConfirmar(chequeo, { calculando: esperando, ahora, recibidoEn: recibido?.en ?? null }) && !confirmando && !fechaFaltante && !horarioIncompleto && !faltaHorarioYBaja
    && modoCuando !== null && !fechaFueraDeRango && !faltaPrueba && !refuerzoPendiente && !sinSaberSiSalio;

  /** con qué fecha se pidió la previa que está a la vista (la de las variables, sin fecha) */
  const previaPedida = useRef<{ previa: Previa; cuando: string | null } | null>(null);
  // La previa real de la tarjeta «Vas a mandar»: la de las variables si ya está y sale ahora; si no (se entró directo, o
  // está programada: un «vence el …» depende de la fecha, 29/10/2026), se pide con la fecha elegida, como el chequeo.
  useEffect(() => {
    if (paso !== "revisar" || !idCampana || previaConfirmar !== "") return;
    const cuandoDeLaPrevia = previa && previaPedida.current?.previa === previa ? previaPedida.current.cuando : null;
    if (previa && cuandoDeLaPrevia === cuandoIso) return;
    setPreviaConfirmar("pidiendo");
    const pedido = cuandoIso;
    void (async () => {
      try {
        const l = leerRespuesta<Previa>(await (pedido ? oficialApi.previa(idCampana, 1, pedido) : oficialApi.previa(idCampana, 1)));
        if (l.ok && l.data) { previaPedida.current = { previa: l.data, cuando: pedido }; setPrevia(l.data); setPreviaConfirmar(""); }
        else setPreviaConfirmar("error");
      } catch {
        setPreviaConfirmar("error");
      }
    })();
  }, [paso, idCampana, previa, previaConfirmar, setPrevia, cuandoIso]);

  /** Sin respuesta del confirmar: cómo quedó la campaña decide qué se ofrece (nunca otra vez a ciegas). */
  const averiguar = async () => {
    if (!idCampana) return;
    setAveriguando(true);
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.campana(idCampana));
      if (!l.ok || !l.data) setRechazo({ texto: TEXTO_NO_SE_SABE, averiguar: true });
      else if (l.data.estado !== "borrador") { setYaConfirmada(true); setRechazo({ texto: TEXTO_SIN_RESPUESTA_CONFIRMADA, linkDetalle: true }); }
      else setRechazo({ texto: TEXTO_SIN_RESPUESTA_BORRADOR, reintentar: true });
    } catch {
      setRechazo({ texto: TEXTO_NO_SE_SABE, averiguar: true });
    } finally {
      setAveriguando(false);
    }
  };

  const confirmar = async () => {
    if (!chequeo || !idCampana || confirmando || sinSaberSiSalio) return;
    if (!puedeConfirmar(chequeo, { calculando: esperando, ahora: new Date(), recibidoEn: recibido?.en ?? null })) return;
    // lo mismo que apaga el botón: sin elegir cuándo, sin prueba ni «Seguir sin probar», o sin la cantidad escrita de un
    // envío grande, no sale
    if (modoCuando === null || fechaFueraDeRango || faltaPrueba || (pideRefuerzo(chequeo) && !refuerzoCumplido(cantidadEscrita, chequeo.envio))) return;
    setConfirmando(true);
    setRechazo(null);
    setAvisoDesactualizado(false);
    setAvisoOtraPestana("");
    try {
      const l = leerRespuesta<ResultadoConfirmar>(await conLimite(oficialApi.confirmar(idCampana, {
        cuando: cuandoIso, huella: chequeo.huella, cantidadEnvio: chequeo.envio, sinPrueba: !pruebaOk, horario,
      }), ESPERA_CONFIRMAR_MS));
      if (!l.ok) {
        const bloqueos = Array.isArray(l.extra.bloqueos) ? (l.extra.bloqueos as CodigoBloqueo[]) : null;
        if (bloqueos?.length) {
          setRechazo({ texto: "No se puede enviar todavía:", bloqueos });
          void calcularChequeo(cuandoIso, horario);
        } else if (l.code === "FUERA_DE_FRANJA") {
          setRechazo({ texto: "Esa hora está fuera de la franja.", proximaApertura: typeof l.extra.proximaApertura === "string" ? l.extra.proximaApertura : undefined });
        } else if (l.code === "FECHA_PASADA") setRechazo({ texto: "Esa hora ya pasó." });
        else if (l.code === "FECHA_INVALIDA") setRechazo({ texto: "Revisá la fecha." });
        else setRechazo({ texto: l.error || textoError(l), paso: pasoQueArregla(l.code) ?? undefined });
        return;
      }
      const r = l.data;
      const aviso = r.estado === "programada"
        ? `Programada para el ${fechaHoraEnZona(r.programadaPara || cuandoIso, zona)}${zona !== ZONA_MARCA ? ` (hora de ${paisDeZona(zona)})` : ""}. Sale sola.`
        : "Empezó el envío. Podés cerrar esta pantalla: sigue sola.";
      navigate(`${B}/campanas/${encodeURIComponent(idCampana)}`, { replace: true, state: { aviso } });
    } catch (e) {
      const x = leerError(e);
      if (x.code === "CHEQUEO_DESACTUALIZADO") {
        // El chequeo nuevo viene en el primer nivel del cuerpo (SPEC-backend §13.0): reemplaza al mostrado.
        const nuevo = x.body.chequeo as Chequeo | undefined;
        if (nuevo && typeof nuevo === "object") { ponerChequeo(nuevo, claveDelPedido(cuandoIso, horario)); setAhora(new Date()); }
        else { ponerChequeo(null); void calcularChequeo(cuandoIso, horario); }
        setAvisoDesactualizado(true);
        // ¿cambió sólo la cuenta, u otra pestaña cambió la campaña? Lo segundo se dice
        void revisarOtraPestana(true);
      } else if (x.code === "ESTADO_INVALIDO") {
        setYaConfirmada(true);
        setRechazo({ texto: TEXTO_YA_CONFIRMADA, linkDetalle: true });
      } else if (esErrorDeClave(x.code)) {
        setRechazo({ texto: TEXTO_ERROR_CLAVE });
      } else if (x.status === 0 || x.status >= 500) {
        // un corte, un 5xx o una respuesta que no llegó: pudo haber salido igual
        await averiguar();
      } else {
        setRechazo({ texto: textoError(x) });
      }
    } finally {
      setConfirmando(false);
    }
  };

  /** Lo de antes cambió (la plantilla, la audiencia, el horario, la aprobación): el chequeo se vuelve a pedir al entrar. */
  const descartarChequeo = useCallback(() => ponerChequeo(null), [ponerChequeo]);
  /** «Reintentar» y «Recalcular»: el chequeo de ahora, con lo elegido. */
  const recalcular = () => { void calcularChequeo(cuandoIso, horario); };

  const motivo = yaConfirmada ? "Ya se confirmó" : averiguando ? "Fijándonos si salió…" : rechazo?.averiguar ? "Fijate si salió antes de volver a mandar"
    : faltaHorarioYBaja || (esperando ? "Calculando…"
    : modoCuando === null ? "Elegí si sale ahora o la programás"
    : fechaFaltante ? "Elegí fecha y hora" : fechaFueraDeRango ? fechaFueraDeRango : horarioIncompleto ? "Revisá el horario de la campaña" : chequeoViejo ? "El cálculo tiene más de 10 minutos"
    : faltaPrueba ? "Mandá una prueba o marcá «Seguir sin probar»"
    : refuerzoPendiente && chequeo ? `${textoRefuerzo(chequeo.envio)} (en «Vas a mandar»)` : "");
  const cta: CtaPaso = {
    label: chequeo ? textoBotonConfirmar(chequeo.envio, cuandoIso, zona) : "Confirmar",
    // apagado también sin motivo escrito: los bloqueos del chequeo ya están a la vista, en el resumen
    motivo: sePuedeConfirmar ? null : motivo,
    onSiguiente: confirmar,
  };

  return {
    cta,
    cuando: {
      modo: modoCuando, local: cuandoLocal, iso: cuandoIso, fechaFaltante, fueraDeRango: fechaFueraDeRango, zona, limites, usarHora, elegirEnRevisar,
      sugerida: sugerida ? textoFechaQueLeToca(sugerida, zona) : null,
      setModo: (m: "ahora" | "programar") => elegirCuando(m, cuandoLocal),
      setLocal: (v: string) => elegirCuando("programar", v),
    },
    horario: { paraEnviar: horario, limiteMeta, errores: erroresDelHorario, incompleto: horarioIncompleto },
    chequeo, calculando, errorChequeo, errorChequeoPaso, chequeoViejo, recalcular, descartarChequeo, ahora,
    previaConfirmar, setPreviaConfirmar, cantidadEscrita, setCantidadEscrita, avisoDesactualizado, avisoOtraPestana, rechazo, confirmando,
    averiguar, averiguando,
  };
}

export type ConfirmarCampana = ReturnType<typeof useConfirmarCampana>;
